import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAtlasSummaryApi, speciesSearchApi, type AtlasSummary, type SpeciesRef } from '../api';
import './Atlas.css';

const PAGE_SIZE = 20;

function categoryLabel(value?: string) {
  return value || '未分类';
}

function speciesEmoji(name: string) {
  if (/虾/.test(name)) return '🦐';
  if (/蟹/.test(name)) return '🦀';
  if (/鱿|墨鱼|章鱼/.test(name)) return '🦑';
  if (/贝/.test(name)) return '🐚';
  if (/藻/.test(name)) return '🌿';
  if (/鳖|龟|蛙|鲵|蛇|蜥/.test(name)) return '🐢';
  if (/参|胆|星/.test(name)) return '⭐';
  if (/鲳|鲤|鳜|鲈|鳙|鲢/.test(name)) return '🐠';
  return '🐟';
}

function AtlasCover({ item }: { item: SpeciesRef }) {
  const [failed, setFailed] = useState(false);
  return item.cover_url && !failed
    ? <img src={item.cover_url} alt={`${item.name} 图片`} loading="lazy" onError={() => setFailed(true)} />
    : <span aria-hidden="true">{speciesEmoji(item.name)}</span>;
}

export default function AtlasPage() {
  const [items, setItems] = useState<SpeciesRef[]>([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('全部');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [summary, setSummary] = useState<AtlasSummary | null>(null);
  const requestVersion = useRef(0);

  useEffect(() => {
    let cancelled = false;
    getAtlasSummaryApi()
      .then((value) => { if (!cancelled) setSummary(value); })
      .catch(() => { if (!cancelled) setSummary(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setFailed(false);
      setLoadingMore(false);
      setLoadMoreFailed(false);
      setItems([]);
      setOffset(0);
      setHasMore(false);
      speciesSearchApi(
        query.trim(),
        PAGE_SIZE,
        0,
        category === '全部' ? undefined : category,
        undefined,
        true,
      )
        .then((data: SpeciesRef[] | { items?: SpeciesRef[] }) => {
          if (cancelled || requestVersion.current !== version) return;
          const page = Array.isArray(data) ? data : (data.items ?? []);
          setItems(page);
          setOffset(page.length);
          setHasMore(page.length === PAGE_SIZE);
          setFailed(false);
        })
        .catch(() => { if (!cancelled && requestVersion.current === version) setFailed(true); })
        .finally(() => { if (!cancelled && requestVersion.current === version) setLoading(false); });
    }, 160);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [category, query]);

  const filtered = items;

  async function loadMore() {
    if (loading || loadingMore || !hasMore) return;
    const version = requestVersion.current;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    try {
      const data = await speciesSearchApi(
        query.trim(),
        PAGE_SIZE,
        offset,
        category === '全部' ? undefined : category,
        undefined,
        true,
      ) as SpeciesRef[] | { items?: SpeciesRef[] };
      if (requestVersion.current !== version) return;
      const page = Array.isArray(data) ? data : (data.items ?? []);
      setItems((previous) => {
        const known = new Set(previous.map((item) => item.id));
        return [...previous, ...page.filter((item) => !known.has(item.id))];
      });
      setOffset((current) => current + page.length);
      setHasMore(page.length === PAGE_SIZE);
    } catch {
      if (requestVersion.current === version) setLoadMoreFailed(true);
    } finally {
      if (requestVersion.current === version) setLoadingMore(false);
    }
  }

  return (
    <div className="container atlas-page">
      <div className="atlas-hero">
        <div>
          <span className="atlas-kicker">{summary ? `${summary.total} 种水产资源` : '水产资源图鉴'} · 原书配图</span>
          <h1>电子图鉴</h1>
          <p>按分类浏览鱼、虾蟹、贝类等资源，查看学名、分布和养殖信息。</p>
        </div>
        <Link className="btn ghost" to="/search">← 返回 AI 搜索</Link>
      </div>

      <div className="atlas-toolbar" role="search">
        <label className="atlas-search">
          <span aria-hidden="true">⌕</span>
          <input
            aria-label="搜索图鉴资源"
            placeholder="搜索资源名称…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="atlas-filters" role="tablist" aria-label="按品类筛选">
          {['全部', ...(summary?.categories.map((item) => item.name) ?? [])].map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={category === option}
              className={`chip outline ${category === option ? 'active' : ''}`}
              onClick={() => setCategory(option)}
            >
              {option === '全部' ? '全部' : categoryLabel(option)}
            </button>
          ))}
        </div>
      </div>

      <div className="atlas-count">
        <span>
          {loading
            ? '正在加载图鉴…'
            : query.trim() || category !== '全部'
              ? `当前已加载 ${filtered.length} 条匹配资源`
              : `目录共 ${summary?.total ?? filtered.length} 条 · 已加载 ${filtered.length} 条`}
        </span>
        {!loading && items.length > 0 && <span className="muted">资料来自 {summary?.books ?? '—'} 册权威名录{hasMore ? ' · 可继续加载' : ''}</span>}
      </div>

      {failed ? (
        <div className="card atlas-empty">图鉴服务暂不可用，请返回搜索页重试。</div>
      ) : loading ? (
        <div className="card atlas-empty">正在加载图鉴资源…</div>
      ) : filtered.length === 0 ? (
        <div className="card atlas-empty">没有匹配的品种，换个关键词或分类试试。</div>
      ) : (
        <div className="atlas-grid">
          {filtered.map((item) => (
            <Link key={item.id} className="atlas-card" to={`/species/${item.id}`}>
              <div className="atlas-cover">
                <AtlasCover item={item} />
              </div>
              <div className="atlas-card-body">
                <h2>{item.name}</h2>
                <span>{categoryLabel(item.category)}</span>
                <b>查看详细资料 →</b>
              </div>
            </Link>
          ))}
        </div>
      )}

      {!loading && !failed && hasMore && (
        <div className="atlas-load-more">
          <button type="button" className="btn ghost" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? '正在加载更多…' : '加载更多品种'}
          </button>
        </div>
      )}
      {loadMoreFailed && <div className="atlas-load-error">加载更多失败，请稍后重试。</div>}

      <div className="footer">@2026 上海渔联网科技 · 电子图鉴</div>
    </div>
  );
}
